import React, { useEffect, useMemo, useRef, useState } from 'react';

import PromptFieldCard from './PromptFieldCard';
import './PromptGraphPlayground.css';

const NODE_WIDTH = 260;
const NODE_HEIGHT = 112;
const CANVAS_HEIGHT = 860;
const CANVAS_WIDTH = 1280;
const MIN_ZOOM = 0.65;
const MAX_ZOOM = 1.65;
const ZOOM_STEP = 0.15;

const getDefaultPosition = (index) => ({
  x: 36 + (index % 4) * 304,
  y: 42 + Math.floor(index / 4) * 224,
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PromptGraphPlayground = ({
  prompts,
  edges,
  selectedPromptId,
  onSelectPrompt,
  onCreateEdge,
  onRemoveEdge,
  storageKey,
}) => {
  const canvasRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [connectMode, setConnectMode] = useState(false);
  const [pendingSourceId, setPendingSourceId] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setPositions(parsed);
      }
    } catch {
      // Ignore malformed stored graph layouts.
    }
  }, [storageKey]);

  useEffect(() => {
    setPositions((prev) => {
      const next = { ...prev };
      prompts.forEach((prompt, index) => {
        if (!next[prompt.id]) {
          next[prompt.id] = getDefaultPosition(index);
        }
      });
      return next;
    });
  }, [prompts]);

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(positions));
    } catch {
      // Ignore storage failures.
    }
  }, [positions, storageKey]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragState || !canvasRef.current) return;

      const bounds = canvasRef.current.getBoundingClientRect();
      const x = clamp(
        (event.clientX - bounds.left) / zoom - dragState.offsetX,
        16,
        CANVAS_WIDTH - NODE_WIDTH - 16
      );
      const y = clamp(
        (event.clientY - bounds.top) / zoom - dragState.offsetY,
        16,
        CANVAS_HEIGHT - NODE_HEIGHT - 16
      );

      setPositions((prev) => ({
        ...prev,
        [dragState.nodeId]: { x, y },
      }));
    };

    const handlePointerUp = () => {
      setDragState(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragState, zoom]);

  const edgeGeometry = useMemo(
    () =>
      edges
        .map((edge) => {
          const source = positions[edge.sourceId];
          const target = positions[edge.targetId];
          if (!source || !target) return null;

          return {
            ...edge,
            x1: source.x + NODE_WIDTH / 2,
            y1: source.y + NODE_HEIGHT / 2,
            x2: target.x + NODE_WIDTH / 2,
            y2: target.y + NODE_HEIGHT / 2,
            midX: (source.x + target.x + NODE_WIDTH) / 2,
            midY: (source.y + target.y + NODE_HEIGHT) / 2,
          };
        })
        .filter(Boolean),
    [edges, positions]
  );

  const handleNodePointerDown = (event, nodeId) => {
    if (connectMode) return;

    const currentPosition = positions[nodeId];
    if (!currentPosition || !canvasRef.current) return;

    const bounds = canvasRef.current.getBoundingClientRect();
    setDragState({
      nodeId,
      offsetX: (event.clientX - bounds.left) / zoom - currentPosition.x,
      offsetY: (event.clientY - bounds.top) / zoom - currentPosition.y,
    });
  };

  const handleNodeClick = (nodeId) => {
    onSelectPrompt?.(nodeId);

    if (!connectMode) return;

    if (!pendingSourceId) {
      setPendingSourceId(nodeId);
      return;
    }

    if (pendingSourceId === nodeId) {
      setPendingSourceId(null);
      return;
    }

    onCreateEdge?.(pendingSourceId, nodeId);
    setPendingSourceId(null);
  };

  const resetConnectMode = () => {
    setConnectMode(false);
    setPendingSourceId(null);
  };

  return (
    <div className="prompt-graph">
      <div className="prompt-graph__header">
        <div>
          <h5 className="mb-1">Prompt graph playground</h5>
          <p className="text-muted mb-0">
            Drag prompt nodes, click two nodes to connect them, and click an edge to remove it.
          </p>
        </div>
        <div className="prompt-graph__controls">
          <div className="prompt-graph__zoom-controls">
            <button
              type="button"
              className="prompt-graph__control"
              onClick={() => setZoom((prev) => clamp(prev - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM))}
            >
              -
            </button>
            <span className="prompt-graph__zoom-label">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className="prompt-graph__control"
              onClick={() => setZoom((prev) => clamp(prev + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM))}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className={`prompt-graph__control ${connectMode ? 'is-active' : ''}`}
            onClick={() => {
              if (connectMode) {
                resetConnectMode();
                return;
              }
              setConnectMode(true);
              setPendingSourceId(null);
            }}
          >
            {connectMode ? 'Cancel Link' : 'Link Nodes'}
          </button>
          {pendingSourceId ? (
            <span className="prompt-graph__status">Choose a target prompt</span>
          ) : (
            <span className="prompt-graph__status">
              {connectMode ? 'Pick a source prompt' : 'Drag with cursor'}
            </span>
          )}
        </div>
      </div>

      <div ref={canvasRef} className="prompt-graph__canvas">
        <div
          className="prompt-graph__viewport"
          style={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            transform: `scale(${zoom})`,
          }}
        >
          <svg
            className="prompt-graph__edges"
            viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
            preserveAspectRatio="none"
          >
            <defs>
              <marker
                id="prompt-graph-arrow"
                markerWidth="10"
                markerHeight="10"
                refX="7"
                refY="3"
                orient="auto"
              >
                <path d="M0,0 L0,6 L8,3 z" fill="rgba(37, 99, 235, 0.9)" />
              </marker>
            </defs>

            {edgeGeometry.map((edge) => (
              <g key={`${edge.sourceId}-${edge.targetId}`}>
                <line
                  x1={edge.x1}
                  y1={edge.y1}
                  x2={edge.x2}
                  y2={edge.y2}
                  className="prompt-graph__edge"
                  markerEnd="url(#prompt-graph-arrow)"
                />
                <circle
                  cx={edge.midX}
                  cy={edge.midY}
                  r="12"
                  className="prompt-graph__edge-remove"
                  onClick={() => onRemoveEdge?.(edge.sourceId, edge.targetId)}
                />
                <text
                  x={edge.midX}
                  y={edge.midY + 4}
                  textAnchor="middle"
                  className="prompt-graph__edge-remove-text"
                  onClick={() => onRemoveEdge?.(edge.sourceId, edge.targetId)}
                >
                  ×
                </text>
              </g>
            ))}
          </svg>

          {prompts.map((prompt) => {
            const position = positions[prompt.id] || getDefaultPosition(0);
            const isSelected = selectedPromptId === prompt.id;
            const isPending = pendingSourceId === prompt.id;
            const isDragging = dragState?.nodeId === prompt.id;

            return (
              <PromptFieldCard
                key={prompt.id}
                className="prompt-graph__node"
                title={prompt.title}
                type={prompt.type || prompt.generationMode || 'Prompt'}
                contextLabel={prompt.contextLabel}
                contextValue={prompt.contextValue}
                selected={isSelected}
                pending={isPending}
                dragging={isDragging}
                linked={Boolean(prompt.linked)}
                compact
                style={{
                  width: NODE_WIDTH,
                  minHeight: NODE_HEIGHT,
                  position: 'absolute',
                  transform: `translate(${position.x}px, ${position.y}px)`,
                }}
                onClick={() => handleNodeClick(prompt.id)}
                onPointerDown={(event) => handleNodePointerDown(event, prompt.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromptGraphPlayground;
